import { useState, useEffect } from 'react'
import { ArrowUp, ArrowDown, ListOrdered } from 'lucide-react'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from '@/common/ui/dialog'
import { Button } from '@/common/ui/button'
import { toast } from 'sonner'
import useQueryGetGeneralSetting from '@/modules/client-setting/api/hooks/useQueryGetGeneralSetting'
import useMutationUpdateGeneralSetting from '@/modules/client-setting/api/hooks/useMutationUpdateGeneralSetting'
import { useQueryClient } from '@tanstack/react-query'
import { GeneralSettingQueryKeys } from '@/modules/client-setting/keys'

const CHANNELS = [
	{ key: 'QR', label: 'QR พร้อมเพย์' },
	{ key: 'TRANSFER', label: 'โอนเงินผ่านธนาคาร' },
	{ key: 'P2P', label: 'P2P' },
]

export default function ChannelReorderModal() {
	const [open, setOpen] = useState(false)
	const [priority, setPriority] = useState<string[]>(['QR', 'TRANSFER', 'P2P'])
	const { data: generalSetting } = useQueryGetGeneralSetting()
	const { mutate: update, isPending } = useMutationUpdateGeneralSetting()
	const queryClient = useQueryClient()

	const enabled = !!(generalSetting as any)?.enableDepositChannelChoice

	useEffect(() => {
		if (generalSetting) {
			setPriority(
				(generalSetting as any).channelPriority || ['QR', 'TRANSFER', 'P2P'],
			)
		}
	}, [generalSetting])

	const move = (index: number, dir: number) => {
		const next = [...priority]
		const swap = index + dir
		if (swap < 0 || swap >= next.length) return
		;[next[index], next[swap]] = [next[swap], next[index]]
		setPriority(next)
	}

	const handleSave = () => {
		update(
			{ payload: { ...generalSetting, channelPriority: priority } },
			{
				onSuccess: () => {
					toast.success('อัพเดทลำดับช่องทางสำเร็จ')
					queryClient.invalidateQueries({
						queryKey: [GeneralSettingQueryKeys.GetGeneralSetting],
					})
					setOpen(false)
				},
				onError: () => toast.error('ไม่สามารถอัพเดทลำดับได้'),
			},
		)
	}

	return (
		<>
			<Button
				variant="outline"
				size="sm"
				onClick={() => setOpen(true)}
				className="gap-1.5"
			>
				<ListOrdered className="h-4 w-4" />
				จัดลำดับช่องทางการฝาก
			</Button>

			<Dialog open={open} onOpenChange={setOpen}>
				<DialogContent className="max-w-sm">
					<DialogHeader>
						<DialogTitle className="flex items-center gap-2">
							<ListOrdered className="h-5 w-5" />
							จัดลำดับช่องทางการฝาก
						</DialogTitle>
						<DialogDescription>
							กำหนดลำดับช่องทางสำรองเมื่อช่องทางหลักไม่พร้อมใช้งาน
						</DialogDescription>
					</DialogHeader>

					<div className="space-y-2">
						{priority.map((ch, i) => {
							const info = CHANNELS.find((c) => c.key === ch)
							return (
								<div
									key={ch}
									className="flex items-center gap-3 rounded-lg border p-3 transition-opacity"
									style={{ opacity: enabled ? 1 : 0.5 }}
								>
									<span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-bold text-muted-foreground">
										{i + 1}
									</span>
									<span className="flex-1 text-sm font-medium">
										{info?.label || ch}
									</span>
									<div className="flex gap-0.5">
										<Button
											variant="ghost"
											size="icon"
											className="h-7 w-7"
											disabled={i === 0}
											onClick={() => move(i, -1)}
										>
											<ArrowUp className="h-3 w-3" />
										</Button>
										<Button
											variant="ghost"
											size="icon"
											className="h-7 w-7"
											disabled={i === priority.length - 1}
											onClick={() => move(i, 1)}
										>
											<ArrowDown className="h-3 w-3" />
										</Button>
									</div>
								</div>
							)
						})}
					</div>

					<DialogFooter>
						<Button variant="outline" onClick={() => setOpen(false)}>
							ยกเลิก
						</Button>
						<Button onClick={handleSave} disabled={isPending}>
							บันทึกลำดับ
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	)
}
